import { createFileRoute, useLocation } from "@tanstack/react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { GradientMesh } from "@/components/ui/gradient-mesh"
import {
  Bot,
  Settings,
  Workflow,
  MessageCircle,
  BarChart,
  FileText,
  Wrench,
  Target,
  TrendingUp,
  CheckCircle,
  Zap,
  Layers,
  Cpu,
} from "lucide-react"
import { SEO } from "@/components/SEO"
import { StructuredData } from "@/components/StructuredData"
import { agentsToolingPage, siteMetadata } from "@/content"

export const Route = createFileRoute("/agents-tooling")({
  component: AgentsToolingPage,
})

const iconMap = {
  bot: Bot,
  settings: Settings,
  workflow: Workflow,
  messageCircle: MessageCircle,
  barChart: BarChart,
  fileText: FileText,
  wrench: Wrench,
  target: Target,
  trendingUp: TrendingUp,
  checkCircle: CheckCircle,
}

function AgentsToolingPage() {
  const location = useLocation()
  const OverviewIcon = iconMap[agentsToolingPage.overview.icon as keyof typeof iconMap]
  const AgentTypesIcon = iconMap[agentsToolingPage.agentTypes.icon as keyof typeof iconMap]
  const ToolsIcon = iconMap[agentsToolingPage.toolsFrameworks.icon as keyof typeof iconMap]
  const ApproachIcon = iconMap[agentsToolingPage.approach.icon as keyof typeof iconMap]
  const BenefitsIcon = iconMap[agentsToolingPage.benefits.icon as keyof typeof iconMap]

  return (
    <>
      <SEO metadata={agentsToolingPage.seo} />
      <StructuredData
        type="service"
        pageTitle="AI Agents & Tooling"
        pageUrl={`${siteMetadata.url}${location.pathname}`}
      />
      <div className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{agentsToolingPage.header.title}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{agentsToolingPage.header.subtitle}</p>
          </div>

          {/* Professional Hero Section */}
          <div className="relative mb-16 overflow-hidden rounded-3xl shadow-2xl">
            <GradientMesh variant="secondary" size="xl" animated={true} className="h-80 md:h-96 lg:h-[28rem]" />

            {/* Floating Elements */}
            <div className="absolute inset-0">
              <div className="absolute top-1/4 left-1/5 animate-float-slow">
                <div className="p-4 glass rounded-2xl">
                  <Workflow className="h-8 w-8 text-blue-500/70" />
                </div>
              </div>
              <div className="absolute top-1/3 right-1/5 animate-float-medium">
                <div className="p-4 glass rounded-2xl">
                  <Cpu className="h-8 w-8 text-cyan-500/70" />
                </div>
              </div>
              <div className="absolute bottom-1/3 left-1/3 animate-float-fast">
                <div className="p-4 glass rounded-2xl">
                  <Zap className="h-8 w-8 text-indigo-500/70" />
                </div>
              </div>
              <div className="absolute bottom-1/4 right-1/3 animate-float-medium">
                <div className="p-4 glass rounded-2xl">
                  <Layers className="h-8 w-8 text-purple-500/70" />
                </div>
              </div>
            </div>

            {/* Central Icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="p-8 glass rounded-full animate-glow">
                <Bot className="h-20 w-20 text-primary" />
              </div>
            </div>

            {/* Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-background/90 via-background/60 to-transparent">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 text-glow">
                Build Competitive Advantages with Custom AI Agents
              </h2>
              <p className="text-muted-foreground text-xl">
                Engineered for scalability and reliability using battle-tested development practices
              </p>
            </div>
          </div>

          {/* Overview */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <OverviewIcon className="h-6 w-6 text-primary" />
                {agentsToolingPage.overview.title}
              </h2>
              <div className="space-y-4 text-muted-foreground">
                {agentsToolingPage.overview.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Agent Types */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold mb-8 flex items-center gap-3">
              <AgentTypesIcon className="h-6 w-6 text-primary" />
              {agentsToolingPage.agentTypes.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {agentsToolingPage.agentTypes.agents.map((agent, index) => {
                const AgentIcon = iconMap[agent.icon as keyof typeof iconMap]
                return (
                  <Card key={index} className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <AgentIcon className="h-6 w-6 text-primary mt-1" />
                        <h3 className="text-lg font-semibold">{agent.title}</h3>
                      </div>
                      <p className="text-muted-foreground mb-4">{agent.description}</p>
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Key Capabilities:</h4>
                        <ul className="space-y-1">
                          {agent.capabilities.map((capability, capIndex) => (
                            <li key={capIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              {capability}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Tools & Frameworks */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <ToolsIcon className="h-6 w-6 text-primary" />
                  {agentsToolingPage.toolsFrameworks.title}
                </h2>
                <div className="space-y-4">
                  {agentsToolingPage.toolsFrameworks.sections.map((section, index) => (
                    <div key={index}>
                      <h3 className="font-medium text-lg mb-2">{section.title}</h3>
                      <p className="text-muted-foreground">{section.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Our Approach */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <ApproachIcon className="h-6 w-6 text-primary" />
                  {agentsToolingPage.approach.title}
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  {agentsToolingPage.approach.content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Benefits */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <BenefitsIcon className="h-6 w-6 text-primary" />
                {agentsToolingPage.benefits.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agentsToolingPage.benefits.items.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">{agentsToolingPage.cta.title}</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">{agentsToolingPage.cta.subtitle}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {agentsToolingPage.cta.buttons.map((button, index) => (
                <Button key={index} asChild variant={button.primary ? "default" : "outline"} size="lg">
                  <a href={button.href}>{button.text}</a>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
